import React, { ChangeEvent, FC, ReactNode, useEffect, useState } from 'react';
import { CreateDealContext } from './context';
import { CreateDealType, DealMetaDataType } from 'models/deals.model';
import { apiService } from 'services/base.service';
import { handleApiError } from 'utils/functions';
import { UserType } from 'models/user.model';
import { BrandType } from 'models/brand.model';
import { MealType } from 'models/meal.model';

const CreateDealProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [createDealForm, setCreateDealForm] = useState<CreateDealType>({
    heading: '',
    title: '',
    body: '',
    cokitchen_ids: [],
    private: false,
    discount_code: '',
    deal_value_type_id: '',
    deal_type_id: '',
    cap: '',
    brands: [],
    min_amount: '',
    min_items: '',
    specific_customers: [],
    notification: false,
    rate: '',
    fixed_amount: '',
    max_usage: '',
    post: false,
    active: false,
    deal_requirement_type_id: '',
    deal_eligibility_type_id: '',
    to_start_date: undefined,
    to_expire_date: undefined,
    to_start_time: null,
    to_expire_time: null,
    meal_ids: []
  });
  const [disabled, setDisabled] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const [dealTypesMetaData, setDealTypesMetaData] = useState({
    dealTypes: [] as DealMetaDataType[],
    dealRequirementTypes: [] as DealMetaDataType[],
    dealValueTypes: [] as DealMetaDataType[],
    dealEligibilityTypes: [] as DealMetaDataType[]
  });

  const [brands, setBrands] = useState<BrandType[]>([]);
  const [meals, setMeals] = useState<MealType[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [userSearchValue, setUserSearchValue] = useState('');
  const [mealBrands, setMealBrands] = useState<string[]>([]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setCreateDealForm((prevFormData) => {
      return {
        ...prevFormData,
        [name]: type === 'checkbox' ? checked : value
      };
    });
  };

  const fetchDealTypes = async () => {
    try {
      const res = await apiService.get('internal/deal-types', {
        isInternal: true
      });
      const {
        deal_types: dealTypes,
        deal_requirement_types: dealRequirementTypes,
        deal_value_types: dealValueTypes,
        deal_eligibility_types: dealEligibilityTypes
      } = res.data.data;
      setDealTypesMetaData({
        dealTypes,
        dealValueTypes,
        dealEligibilityTypes,
        dealRequirementTypes
      });
    } catch (error) {
      setDealTypesMetaData({
        dealTypes: [],
        dealRequirementTypes: [],
        dealValueTypes: [],
        dealEligibilityTypes: []
      });
      handleApiError(error);
    }
  };

  const fetchBrands = async () => {
    if (!createDealForm.cokitchen_ids.length) return;
    try {
      const res = await apiService.post('brands/fetch', {
        data: {
          cokitchen_ids: createDealForm.cokitchen_ids
        }
      });
      setBrands(res.data.data);
    } catch (error) {
      setBrands([]);
      handleApiError(error);
    }
  };
  const fetchMeals = async () => {
    if (!mealBrands.length) return;
    try {
      const res = await apiService.post('marketing/meals-multiple-brand_id', {
        data: {
          brand_ids: mealBrands
        }
      });
      setMeals(res.data.data);
    } catch (error) {
      setMeals([]);
      handleApiError(error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await apiService.get('marketing/user', {
        params: {
          search: userSearchValue
        }
      });
      setUsers(res.data.data);
    } catch (error) {
      setUsers([]);
      handleApiError(error);
    }
  };

  useEffect(() => {
    fetchDealTypes();
  }, []);

  useEffect(() => {
    fetchBrands();
  }, [createDealForm.cokitchen_ids]);

  useEffect(() => {
    fetchMeals();
  }, [mealBrands]);

  useEffect(() => {
    fetchUsers();
  }, [userSearchValue]);

  return (
    <CreateDealContext.Provider
      value={{
        createDealForm,
        setCreateDealForm,
        disabled,
        setDisabled,
        handleChange,
        dealValueTypes: dealTypesMetaData.dealValueTypes,
        dealTypes: dealTypesMetaData.dealTypes,
        brands,
        dealRequirementTypes: dealTypesMetaData.dealRequirementTypes,
        dealEligibilityTypes: dealTypesMetaData.dealEligibilityTypes,
        users,
        setUserSearchValue,
        mealBrands,
        setMealBrands,
        meals,
        images,
        setImages
      }}>
      {children}
    </CreateDealContext.Provider>
  );
};

export default CreateDealProvider;
